// API Configuration with Environment Variables
const API_CONFIG = {
  // Base URL - Use relative path for single-origin setup
  BASE_URL: process.env.REACT_APP_API_URL || '/api',
  
  // Timeout from environment variable
  TIMEOUT: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,
  
  // Environment from environment variable
  NODE_ENV: process.env.NODE_ENV || 'development',
  
  // Feature flags from environment variables
  ENABLE_DEBUG: process.env.REACT_APP_ENABLE_DEBUG === 'true',
  ENABLE_LOGGING: process.env.REACT_APP_ENABLE_LOGGING === 'true',
  
  // API Endpoints
  ENDPOINTS: {
    AUTH: '/admin',
    PLAYERS: '/players',
    EVENTS: '/events',
    GALLERY: '/gallery',
    PAYMENT: '/payment',
    HEALTH: '/health',
    PaymentNew: '/paymentNew',
    PlayerNew: '/playerNew',
  },
  
  // Default headers
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },

  // Image URL helper function
  getImageUrl: (imagePath) => {
    if (!imagePath) return '/images/heroSection_img.jpg';
    if (imagePath.startsWith('http')) return imagePath; // Absolute URL
    if (imagePath.startsWith('/uploads/')) return imagePath; // Already a full path
    return `/uploads/gallery/${imagePath}`; // Prepend for filenames
  }
};

export const buildApiUrl = (endpoint, path = '') => {
  return `${API_CONFIG.BASE_URL}${endpoint}${path}`;
};

export const API_URLS = {
  // Health Check
  HEALTH_CHECK: `${API_CONFIG.BASE_URL}/api/health`,
  
  // Admin Endpoints
  ADMIN_LOGIN: buildApiUrl(API_CONFIG.ENDPOINTS.AUTH, '/login'),
  ADMIN_REGISTER: buildApiUrl(API_CONFIG.ENDPOINTS.AUTH, '/register'),
  ADMIN_VERIFY: buildApiUrl(API_CONFIG.ENDPOINTS.AUTH, '/verify'),
  ADMIN_DEBUG: `${API_CONFIG.BASE_URL}/api/debug/admin`,
  
  // Player Endpoints
  PLAYER_REGISTER: buildApiUrl(API_CONFIG.ENDPOINTS.PLAYERS, '/register'),
  PLAYER_LIST: buildApiUrl(API_CONFIG.ENDPOINTS.PLAYERS, ''),
  PLAYER_BY_NAME: buildApiUrl(API_CONFIG.ENDPOINTS.PLAYERS, '/name'),
  PLAYER_BY_MOBILE: buildApiUrl(API_CONFIG.ENDPOINTS.PLAYERS, '/mobile'),
  PLAYER_CHECK_MOBILE: buildApiUrl(API_CONFIG.ENDPOINTS.PLAYERS, '/check-mobile'),
  PLAYER_DELETE: buildApiUrl(API_CONFIG.ENDPOINTS.PLAYERS, '/delete'),
  PLAYER_EXPORT: buildApiUrl(API_CONFIG.ENDPOINTS.PLAYERS, '/export'),

  // Player New Endpoints
  PLAYER_NEW_REGISTER: buildApiUrl(API_CONFIG.ENDPOINTS.PlayerNew, '/register'),
  PLAYER_NEW_LIST: buildApiUrl(API_CONFIG.ENDPOINTS.PlayerNew, ''),
  PLAYER_NEW_BY_NAME: buildApiUrl(API_CONFIG.ENDPOINTS.PlayerNew, '/name'),
  PLAYER_NEW_BY_MOBILE: buildApiUrl(API_CONFIG.ENDPOINTS.PlayerNew, '/mobile'),
  PLAYER_NEW_CHECK_MOBILE: buildApiUrl(API_CONFIG.ENDPOINTS.PlayerNew, '/check-mobile'),
  PLAYER_NEW_DELETE: buildApiUrl(API_CONFIG.ENDPOINTS.PlayerNew, '/delete'),
  PLAYER_NEW_EXPORT: buildApiUrl(API_CONFIG.ENDPOINTS.PlayerNew, '/export'),
  
  // Event Endpoints
  EVENT_CREATE: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, ''),
  EVENT_LIST: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, '/all'),
  EVENT_UPDATE: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, '/update'),
  EVENT_DELETE: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, '/delete'),
  
  // Upcoming Events
  UPCOMING_CREATE: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, '/upcoming/create'),
  UPCOMING_LIST: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, '/upcoming/list'),
  UPCOMING_PUBLIC: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, '/all-public'),
  UPCOMING_DELETE: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, '/upcoming/delete'),
  UPCOMING_FOR_REGISTRATION: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, '/upcoming-for-registration'),
  
  // Public Events (no authentication required)
  PUBLIC_EVENTS: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, '/public'),
  ALL_PUBLIC_EVENTS: buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS, '/all-public'),
  
  // Gallery Endpoints
  GALLERY_UPLOAD: buildApiUrl(API_CONFIG.ENDPOINTS.GALLERY, '/upload'),
  GALLERY_LIST: buildApiUrl(API_CONFIG.ENDPOINTS.GALLERY, '/all'),
  GALLERY_DELETE: buildApiUrl(API_CONFIG.ENDPOINTS.GALLERY, '/delete'),
  
    // Payment Endpoints
    PAYMENT_CONFIG: buildApiUrl(API_CONFIG.ENDPOINTS.PAYMENT, '/config'),
    PAYMENT_FEES: buildApiUrl(API_CONFIG.ENDPOINTS.PAYMENT, '/fees'),
    PAYMENT_INITIATE: buildApiUrl(API_CONFIG.ENDPOINTS.PAYMENT, '/initiate'),
    PAYMENT_HEALTH: buildApiUrl(API_CONFIG.ENDPOINTS.PAYMENT, '/health'),
    PAYMENT_STATUS: buildApiUrl(API_CONFIG.ENDPOINTS.PAYMENT, '/status'),
    PAYMENT_RECEIPT: buildApiUrl(API_CONFIG.ENDPOINTS.PAYMENT, '/receipt'),
    PAYMENT_RECEIPT_BY_TXN: buildApiUrl(API_CONFIG.ENDPOINTS.PAYMENT, '/receipt/txn'),

    // Payment New Endpoints
    PAYMENT_NEW_CONFIG: buildApiUrl(API_CONFIG.ENDPOINTS.PaymentNew, '/config'),
    PAYMENT_NEW_FEES: buildApiUrl(API_CONFIG.ENDPOINTS.PaymentNew, '/fees'),
    PAYMENT_NEW_INITIATE: buildApiUrl(API_CONFIG.ENDPOINTS.PaymentNew, '/initiate'),
    PAYMENT_NEW_HEALTH: buildApiUrl(API_CONFIG.ENDPOINTS.PaymentNew, '/health'),
    PAYMENT_NEW_STATUS: buildApiUrl(API_CONFIG.ENDPOINTS.PaymentNew, '/status'),
    PAYMENT_NEW_RECEIPT: buildApiUrl(API_CONFIG.ENDPOINTS.PaymentNew, '/receipt'),
    PAYMENT_NEW_RECEIPT_BY_TXN: buildApiUrl(API_CONFIG.ENDPOINTS.PaymentNew, '/receipt/txn'),
  
  // File Upload
  UPLOAD_EVENTS: `${API_CONFIG.BASE_URL}/uploads/events/`,
  UPLOAD_GALLERY: `${API_CONFIG.BASE_URL}/uploads/gallery/`,
};

// API Helper Functions
export const apiRequest = async (url, options = {}) => {
  // Check token expiry before making request
  const token = localStorage.getItem('adminToken');
  const tokenExpiry = localStorage.getItem('tokenExpiry');
  
  if (token && tokenExpiry) {
    const now = new Date().getTime();
    const expiryTime = parseInt(tokenExpiry);
    
    if (now >= expiryTime) {
      // Token expired, clear it and redirect to login
      localStorage.removeItem('adminToken');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('rememberMe');
      window.dispatchEvent(new Event('admin-logout'));
      window.location.href = '/admin';
      throw new Error('Session expired. Please login again.');
    }
  }

  const config = {
    method: 'GET',
    headers: {
      ...API_CONFIG.DEFAULT_HEADERS,
      ...options.headers,
    },
    ...options,
  };

  // Add authorization header if token exists
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      throw new Error(`https error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    // console.log('API Request Error:', error);
    throw error;
  }
};

// Specific API functions
export const apiService = {
  // Health Check
  healthCheck: () => apiRequest(API_URLS.HEALTH_CHECK),
  
  // Admin Authentication
  adminLogin: (credentials) => apiRequest(API_URLS.ADMIN_LOGIN, {
    method: 'POST',
    body: JSON.stringify(credentials),
  }),
  
  adminVerify: (token) => apiRequest(API_URLS.ADMIN_VERIFY, {
    method: 'POST',
    body: JSON.stringify({ token }),
  }),
  
  // Player Management
  registerPlayer: (playerData) => apiRequest(API_URLS.PLAYER_REGISTER, {
    method: 'POST',
    body: JSON.stringify(playerData),
  }),
  
  getPlayers: () => apiRequest(API_URLS.PLAYER_LIST),
  
  deletePlayer: (playerId) => apiRequest(API_URLS.PLAYER_DELETE, {
    method: 'DELETE',
    body: JSON.stringify({ playerId }),
  }),
  
  exportPlayers: () => apiRequest(API_URLS.PLAYER_EXPORT),
  
  // Event Management
  createEvent: (eventData) => apiRequest(API_URLS.EVENT_CREATE, {
    method: 'POST',
    body: JSON.stringify(eventData),
  }),
  
  getEvents: () => apiRequest(API_URLS.EVENT_LIST),
  
  updateEvent: (eventData) => apiRequest(API_URLS.EVENT_UPDATE, {
    method: 'PUT',
    body: JSON.stringify(eventData),
  }),
  
  deleteEvent: (eventId) => apiRequest(API_URLS.EVENT_DELETE, {
    method: 'DELETE',
    body: JSON.stringify({ eventId }),
  }),
  
  // Gallery Management
  uploadGallery: (formData) => apiRequest(API_URLS.GALLERY_UPLOAD, {
    method: 'POST',
    headers: {}, // Let browser set Content-Type for FormData
    body: formData,
  }),
  
  getGallery: () => apiRequest(API_URLS.GALLERY_LIST),
  
  deleteGallery: (imageId) => apiRequest(API_URLS.GALLERY_DELETE, {
    method: 'DELETE',
    body: JSON.stringify({ imageId }),
  }),
};

export default API_CONFIG;